<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import CustomStore from 'devextreme/data/custom_store';
import type { LoadOptions } from 'devextreme/data';
import DxForm, { DxSimpleItem, DxLabel } from 'devextreme-vue/form';
import { DxButton } from 'devextreme-vue/button';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxTagBox } from 'devextreme-vue/tag-box';
import { DxDateBox } from 'devextreme-vue/date-box';
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxPager,
  DxSelection,
  DxMasterDetail,
  DxSummary,
  DxTotalItem,
  DxColumnChooser,
  DxStateStoring,
} from 'devextreme-vue/data-grid';
import {
  getAggregatorsLookup,
  bottoGridPaging,
  BottoPagePanel,
  LookupBox,
  showError,
  showSuccess,
  env,
  DEFAULT_GRID_PAGE_SIZE,
  GRID_PAGE_SIZES,
  getAuthUsersLookup,
  authService,
  getMessageTypesLookup,
  getCountriesLookup,
  getCurrenciesLookup,
} from '@botto/core';
import {
  exportStatsExcel,
  fetchMessageDetails,
  fetchStatsReport,
  formatCountWithPct,
  formatDateForApi,
  formatMoney,
  REPORT_GROUP_BY_OPTIONS,
  type DetailsQuery,
  type MessageLogRow,
  type ReportGroupBy,
  type StatsReportQuery,
  type StatsReportRow,
  type StatsRow,
} from '../../api';
import {
  getManagersLookup,
  getPartnersLookup,
  getSmppClientsLookup,
  reloadAccountsLookups,
} from '../../api/adapters/accounts';
import { getDirectionsLookup } from '../../lookups';
import { fraudScoreCellTemplate } from '../../utils/fraudScore';
import MessageDetailHistoryPanel from './MessageDetailHistoryPanel.vue';

const { paging, pager } = bottoGridPaging();

const statsGridStateKey = `botto.sms.stats.grid.${authService.getDisplayName() || 'anon'}`;
const detailsGridStateKey = `botto.sms.stats.details.grid.${authService.getDisplayName() || 'anon'}`;

const directionsLookup = getDirectionsLookup();
const aggregatorsLookup = getAggregatorsLookup();
const clientsLookup = getSmppClientsLookup();
const managersLookup = getManagersLookup();
const partnersLookup = getPartnersLookup();
const authUsersLookup = getAuthUsersLookup();
const messageTypesLookup = getMessageTypesLookup();
const countriesLookup = getCountriesLookup();
const currenciesLookup = getCurrenciesLookup();

const loading = ref(false);
const exporting = ref(false);

const statsRows = ref<StatsReportRow[]>([]);
const statsTotal = ref<StatsRow | null>(null);
const activeGroupBy = ref<ReportGroupBy[]>(['created_day']);
const selectedRow = ref<StatsReportRow | null>(null);
const selectedRowKeys = ref<string[]>([]);
const detailsGrid = ref<InstanceType<typeof DxDataGrid> | null>(null);
const detailTotal = ref(0);

/** Group dims with no matching /details/ query param. */
const DETAILS_UNSUPPORTED_GROUP: ReportGroupBy[] = ['mcc', 'mnc'];

const STATUS_OPTIONS = [
  { value: null, text: 'Все' },
  { value: 'PENDING', text: 'PENDING' },
  { value: 'ROUTED', text: 'ROUTED' },
  { value: 'SENT', text: 'SENT' },
  { value: 'REJECTED', text: 'REJECTED' },
  { value: 'EXPIRED', text: 'EXPIRED' },
  { value: 'UNDELIV', text: 'UNDELIV' },
  { value: 'DELIVRD', text: 'DELIVRD' },
];

const GROUP_BY_LABELS = Object.fromEntries(
  REPORT_GROUP_BY_OPTIONS.map((o) => [o.value, o.label]),
) as Record<ReportGroupBy, string>;

const groupByDropDownOptions = {
  container: typeof document !== 'undefined' ? document.body : undefined,
  hideOnParentScroll: false,
  maxHeight: 360,
  wrapperAttr: { class: 'stats-groupby-overlay' },
};

const filters = ref<{
  date_from: Date | null;
  date_to: Date | null;
  group_by: ReportGroupBy[];
  report_currency: string;
  client_id: number | null;
  manager_id: number | null;
  partner_id: number | null;
  direction_id: number | null;
  aggregator_id: number | null;
  country_iso: string | null;
  status: string | null;
  phone_to: string;
  phone_from: string;
  user_id: number | null;
  system_id: string;
  message_id: string;
  correlation_id: string;
}>({
  date_from: null,
  date_to: null,
  group_by: ['created_day'],
  report_currency: 'RUB',
  client_id: null,
  manager_id: null,
  partner_id: null,
  direction_id: null,
  aggregator_id: null,
  country_iso: null,
  status: null,
  phone_to: '',
  phone_from: '',
  user_id: null,
  system_id: '',
  message_id: '',
  correlation_id: '',
});

const LOOKUP_GROUP_FIELDS = new Set<ReportGroupBy>([
  'user_id',
  'manager_id',
  'partner_id',
  'aggregator_id',
  'direction_id',
  'country_iso',
  'message_type',
]);

const groupColumns = computed(() =>
  (activeGroupBy.value.length ? activeGroupBy.value : (['created_day'] as ReportGroupBy[])).map(
    (field) => ({
      dataField: `group.${field}`,
      caption: GROUP_BY_LABELS[field] ?? field,
      resolveLabel: LOOKUP_GROUP_FIELDS.has(field),
      field,
    }),
  ),
);

function groupCellDisplay(field: ReportGroupBy, row: StatsReportRow): string {
  const raw = row.group?.[field];
  if (raw == null || raw === '') return '';
  if (field === 'user_id') return authUsersLookup.labelOf(raw as number | string);
  if (field === 'manager_id') return managersLookup.labelOf(raw as number | string);
  if (field === 'partner_id') return partnersLookup.labelOf(raw as number | string);
  if (field === 'aggregator_id') return aggregatorsLookup.labelOf(raw as number | string);
  if (field === 'direction_id') return directionsLookup.labelOf(raw as number | string);
  if (field === 'country_iso') {
    const iso = String(raw);
    const label = countriesLookup.labelOf(iso);
    if (label !== iso) return label;
    return countriesLookup.labelOf(iso.toLowerCase()) || countriesLookup.labelOf(iso.toUpperCase());
  }
  if (field === 'message_type') return messageTypesLookup.labelOf(raw as number | string);
  return String(raw);
}

const unsupportedDetailsGroup = computed(() =>
  activeGroupBy.value.filter((f) => DETAILS_UNSUPPORTED_GROUP.includes(f)),
);

const detailsCaption = computed(() => {
  if (!selectedRow.value) return 'Детализация';
  const parts: string[] = [];
  for (const field of activeGroupBy.value) {
    const label = groupCellDisplay(field, selectedRow.value);
    if (label) parts.push(label);
  }
  return parts.length ? `Детализация · ${parts.join(', ')}` : 'Детализация';
});

function nextDayIso(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function statsRowKey(row: StatsReportRow): string {
  return JSON.stringify(row.group);
}

function inDeliveryDisplay(row: StatsReportRow) {
  return formatCountWithPct(row.in_delivery, row.in_delivery_pct);
}
function deliveredDisplay(row: StatsReportRow) {
  return formatCountWithPct(row.delivered, row.delivered_pct);
}
function undeliveredDisplay(row: StatsReportRow) {
  return formatCountWithPct(row.undelivered, row.undelivered_pct);
}
function expiredDisplay(row: StatsReportRow) {
  return formatCountWithPct(row.expired, row.expired_pct);
}
function customizeMoneyText(e: { value?: number }) {
  return formatMoney(e.value ?? 0, filters.value.report_currency || 'RUB');
}

function customizeUnitPriceText(e: { value?: number | null }) {
  if (e.value == null) return '';
  return formatMoney(Number(e.value), filters.value.report_currency || 'RUB', 5);
}

function customizeClickRateText(e: { value?: number | null }) {
  if (e.value == null) return '';
  const n = Number(e.value);
  if (!Number.isFinite(n)) return '';
  const pct = Math.round(n * 10000) / 100;
  return `${pct.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function customizeSummaryMoney(e: { value: string | number | Date; valueText: string }) {
  const n = typeof e.value === 'number' ? e.value : Number(e.value);
  if (!Number.isFinite(n)) return e.valueText;
  return formatMoney(n, filters.value.report_currency || 'RUB');
}

function customizeSummaryClickRate(e: { value: string | number | Date; valueText: string }) {
  const n = typeof e.value === 'number' ? e.value : Number(e.value);
  if (!Number.isFinite(n)) return e.valueText;
  return customizeClickRateText({ value: n });
}

function calculateStatsSummary(e: {
  name?: string;
  summaryProcess?: string;
  totalValue?: unknown;
}) {
  if (e.summaryProcess !== 'finalize') return;
  const t = statsTotal.value;
  if (!t) {
    e.totalValue = undefined;
    return;
  }
  switch (e.name) {
    case 'sum_total':
      e.totalValue = t.total;
      break;
    case 'sum_in_delivery':
      e.totalValue = formatCountWithPct(t.in_delivery, t.in_delivery_pct);
      break;
    case 'sum_delivered':
      e.totalValue = formatCountWithPct(t.delivered, t.delivered_pct);
      break;
    case 'sum_undelivered':
      e.totalValue = formatCountWithPct(t.undelivered, t.undelivered_pct);
      break;
    case 'sum_expired':
      e.totalValue = formatCountWithPct(t.expired, t.expired_pct);
      break;
    case 'sum_fraud':
      e.totalValue = t.fraud_score;
      break;
    case 'sum_clicks':
      e.totalValue = t.clicks;
      break;
    case 'sum_click_rate':
      e.totalValue = t.click_rate;
      break;
    case 'sum_sell_price':
      e.totalValue = t.sell_price_per_message;
      break;
    case 'sum_buy_price':
      e.totalValue = t.buy_price_per_message;
      break;
    case 'sum_sell_total':
      e.totalValue = t.sell_total;
      break;
    case 'sum_sell_refund':
      e.totalValue = t.sell_refund;
      break;
    case 'sum_buy_total':
      e.totalValue = t.buy_total;
      break;
    case 'sum_buy_refund':
      e.totalValue = t.buy_refund;
      break;
    case 'sum_spent':
      e.totalValue = t.spent;
      break;
    case 'sum_profit':
      e.totalValue = t.profit;
      break;
    default:
      e.totalValue = undefined;
  }
}

function setFilter<K extends keyof typeof filters.value>(
  field: K,
  value: (typeof filters.value)[K],
) {
  filters.value[field] = value;
}

function onGroupByChanged(e: { value?: ReportGroupBy[] }) {
  setFilter('group_by', e.value?.length ? e.value : ['created_day']);
}

function buildReportQuery(): StatsReportQuery {
  return {
    group_by: filters.value.group_by?.length ? filters.value.group_by : ['created_day'],
    report_currency: filters.value.report_currency || 'RUB',
    client_id: filters.value.client_id,
    manager_id: filters.value.manager_id,
    partner_id: filters.value.partner_id,
    direction_id: filters.value.direction_id,
    aggregator_id: filters.value.aggregator_id,
    country_iso: filters.value.country_iso?.trim() || null,
    date_from: formatDateForApi(filters.value.date_from),
    date_to: formatDateForApi(filters.value.date_to),
  };
}

function asFiniteNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function asNonEmptyString(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

/** Report filters + selected row group dims → GET /details/ query. */
function buildDetailsQuery(row?: StatsReportRow | null): DetailsQuery {
  const f = filters.value;
  const q: DetailsQuery = {
    date_from: formatDateForApi(f.date_from),
    date_to: formatDateForApi(f.date_to),
    status: f.status,
    phone_to: f.phone_to.trim() || null,
    phone_from: f.phone_from.trim() || null,
    user_id: f.user_id ?? f.client_id,
    system_id: f.system_id.trim() || null,
    message_id: f.message_id.trim() || null,
    correlation_id: f.correlation_id.trim() || null,
    manager_id: f.manager_id,
    partner_id: f.partner_id,
    direction_id: f.direction_id,
    aggregator_id: f.aggregator_id,
    country_iso: f.country_iso?.trim() || null,
  };

  if (!row) return q;

  const g = row.group;

  const dayRaw = g.created_day;
  const day = dayRaw != null && dayRaw !== '' ? String(dayRaw).slice(0, 10) : '';
  if (day && /^\d{4}-\d{2}-\d{2}$/.test(day)) {
    q.date_from = `${day} 00:00:00`;
    q.date_to = `${nextDayIso(day)} 00:00:00`;
  }

  const uid = asFiniteNumber(g.user_id);
  if (uid != null) q.user_id = uid;

  const managerId = asFiniteNumber(g.manager_id);
  if (managerId != null) q.manager_id = managerId;

  const partnerId = asFiniteNumber(g.partner_id);
  if (partnerId != null) q.partner_id = partnerId;

  const aggregatorId = asFiniteNumber(g.aggregator_id);
  if (aggregatorId != null) q.aggregator_id = aggregatorId;

  const directionId = asFiniteNumber(g.direction_id);
  if (directionId != null) q.direction_id = directionId;

  const countryIso = asNonEmptyString(g.country_iso);
  if (countryIso) q.country_iso = countryIso;

  const messageType = asFiniteNumber(g.message_type);
  if (messageType != null) q.message_type = messageType;

  // carrier_codes ≈ operator filter on /details/
  const carrier = asNonEmptyString(g.carrier_codes);
  if (carrier) q.operator = carrier;

  return q;
}

const detailsStore = new CustomStore<MessageLogRow>({
  key: '__key',
  loadMode: 'processed',
  async load(loadOptions: LoadOptions) {
    const row = selectedRow.value;
    if (!row) {
      detailTotal.value = 0;
      return { data: [], totalCount: 0 };
    }
    const skip = typeof loadOptions.skip === 'number' ? loadOptions.skip : 0;
    const take =
      typeof loadOptions.take === 'number' ? loadOptions.take : env.defaultPageSize;
    try {
      const details = await fetchMessageDetails({
        ...buildDetailsQuery(row),
        offset: skip,
        page_size: take,
      });
      detailTotal.value = details.total;
      return { data: details.items, totalCount: details.total };
    } catch (e) {
      showError(e);
      detailTotal.value = 0;
      return { data: [], totalCount: 0 };
    }
  },
});

function reloadDetails() {
  const instance = detailsGrid.value?.instance;
  if (!instance) return;
  if (instance.pageIndex() !== 0) {
    instance.pageIndex(0);
    return;
  }
  void instance.refresh();
}

function applyDetailsFilters() {
  if (!selectedRow.value) return;
  reloadDetails();
}

function clearDetails() {
  selectedRow.value = null;
  selectedRowKeys.value = [];
  detailTotal.value = 0;
  reloadDetails();
}

async function load() {
  loading.value = true;
  try {
    clearDetails();
    const stats = await fetchStatsReport(buildReportQuery());
    activeGroupBy.value = stats.group_by;
    statsRows.value = stats.items.map((r) => ({ ...r, __key: statsRowKey(r) }));
    statsTotal.value = stats.total;
  } catch (e) {
    showError(e);
    statsTotal.value = null;
  } finally {
    loading.value = false;
  }
}

function onStatsSelectionChanged(e: { selectedRowsData?: StatsReportRow[] }) {
  const row = e.selectedRowsData?.[0] ?? null;
  selectedRow.value = row;
  selectedRowKeys.value = row ? [statsRowKey(row)] : [];
  reloadDetails();
}

async function exportStats() {
  exporting.value = true;
  try {
    await exportStatsExcel(buildReportQuery());
    showSuccess('Файл статистики скачан');
  } catch (e) {
    showError(e);
  } finally {
    exporting.value = false;
  }
}

onMounted(() => {
  void directionsLookup.ensureLoaded();
  void aggregatorsLookup.ensureLoaded();
  void authUsersLookup.ensureLoaded();
  void messageTypesLookup.ensureLoaded();
  void countriesLookup.ensureLoaded();
  void currenciesLookup.ensureLoaded();
  void reloadAccountsLookups();
});
</script>

<template>
  <BottoPagePanel title="Статистика" standalone>
    <div class="stats-page">
      <div>
        <div class="stats-section__header">
          <h3 class="stats-section__title">Период и группировка</h3>
        </div>
        <div class="stats-filters-grid">
          <div class="stats-field">
            <label>Дата с</label>
            <DxDateBox
              :value="filters.date_from"
              type="datetime"
              display-format="dd.MM.yyyy HH:mm"
              width="100%"
              @value-changed="(e: any) => setFilter('date_from', e.value ?? null)"
            />
          </div>
          <div class="stats-field">
            <label>Дата по</label>
            <DxDateBox
              :value="filters.date_to"
              type="datetime"
              display-format="dd.MM.yyyy HH:mm"
              width="100%"
              @value-changed="(e: any) => setFilter('date_to', e.value ?? null)"
            />
          </div>
          <div class="stats-field">
            <label>Валюта</label>
            <LookupBox
              :model-value="filters.report_currency"
              :lookup="currenciesLookup"
              placeholder="Валюта"
              :show-clear-button="false"
              @update:model-value="(v) => setFilter('report_currency', (v as string) || 'RUB')"
            />
          </div>
          <div class="stats-field stats-field--wide">
            <label>Группировка</label>
            <DxTagBox
              :value="filters.group_by"
              :items="REPORT_GROUP_BY_OPTIONS"
              value-expr="value"
              display-expr="label"
              placeholder="Выберите поля группировки"
              :show-selection-controls="true"
              :search-enabled="true"
              :show-drop-down-button="true"
              :show-clear-button="true"
              apply-value-mode="instantly"
              :drop-down-options="groupByDropDownOptions"
              @value-changed="onGroupByChanged"
            />
          </div>
        </div>
      </div>

      <div class="stats-section">
        <div class="stats-section__header">
          <h3 class="stats-section__title">Фильтры отчёта</h3>
          <DxButton
            text="Сформировать"
            type="default"
            styling-mode="contained"
            :disabled="loading"
            @click="load"
          />
        </div>
        <div class="stats-filters-grid">
          <div class="stats-field">
            <label>Канал</label>
            <LookupBox
              :model-value="filters.client_id"
              :lookup="clientsLookup"
              placeholder="SMPP-канал"
              @update:model-value="(v) => setFilter('client_id', v as number | null)"
            />
          </div>
          <div class="stats-field">
            <label>Менеджер</label>
            <LookupBox
              :model-value="filters.manager_id"
              :lookup="managersLookup"
              placeholder="Менеджер"
              @update:model-value="(v) => setFilter('manager_id', v as number | null)"
            />
          </div>
          <div class="stats-field">
            <label>Партнёр</label>
            <LookupBox
              :model-value="filters.partner_id"
              :lookup="partnersLookup"
              placeholder="Партнёр"
              @update:model-value="(v) => setFilter('partner_id', v as number | null)"
            />
          </div>
          <div class="stats-field">
            <label>Направление</label>
            <LookupBox
              :model-value="filters.direction_id"
              :lookup="directionsLookup"
              placeholder="Направление"
              @update:model-value="(v) => setFilter('direction_id', v as number | null)"
            />
          </div>
          <div class="stats-field">
            <label>Агрегатор</label>
            <LookupBox
              :model-value="filters.aggregator_id"
              :lookup="aggregatorsLookup"
              placeholder="Агрегатор"
              @update:model-value="(v) => setFilter('aggregator_id', v as number | null)"
            />
          </div>
          <div class="stats-field">
            <label>Country ISO</label>
            <LookupBox
              :model-value="filters.country_iso"
              :lookup="countriesLookup"
              placeholder="Country ISO"
              @update:model-value="(v) => setFilter('country_iso', v as string | null)"
            />
          </div>
        </div>
      </div>

      <section class="stats-section">
        <div class="stats-section__header">
          <h3 class="stats-section__title">Статистика SMS</h3>
          <DxButton
            text="Экспорт в Excel"
            icon="xlsxfile"
            styling-mode="outlined"
            :disabled="loading || exporting"
            @click="exportStats"
          />
        </div>
        <DxDataGrid
          :data-source="statsRows"
          :show-borders="true"
          :row-alternation-enabled="true"
          :column-auto-width="true"
          :allow-column-reordering="true"
          :allow-column-resizing="true"
          :disabled="loading"
          :hover-state-enabled="true"
          key-expr="__key"
          :selected-row-keys="selectedRowKeys"
          no-data-text="Нет данных. Выберите период и нажмите «Сформировать»."
          @selection-changed="onStatsSelectionChanged"
        >
          <DxColumnChooser :enabled="true" mode="select" title="Столбцы" />
          <DxStateStoring
            :enabled="true"
            type="localStorage"
            :storage-key="statsGridStateKey"
          />
          <DxSelection mode="single" />
          <DxColumn
            v-for="col in groupColumns"
            :key="col.dataField"
            :data-field="col.dataField"
            :caption="col.caption"
            :calculate-cell-value="
              col.resolveLabel
                ? (row: StatsReportRow) => groupCellDisplay(col.field, row)
                : undefined
            "
          />
          <DxColumn data-field="total" caption="Всего сообщений" data-type="number" />
          <DxColumn
            name="col_in_delivery"
            caption="В процессе доставки (отправлено) (%)"
            :calculate-cell-value="inDeliveryDisplay"
            :allow-sorting="false"
          />
          <DxColumn
            name="col_delivered"
            caption="Доставлено (%)"
            :calculate-cell-value="deliveredDisplay"
            :allow-sorting="false"
          />
          <DxColumn
            name="col_undelivered"
            caption="Не доставлено (%)"
            :calculate-cell-value="undeliveredDisplay"
            :allow-sorting="false"
          />
          <DxColumn
            name="col_expired"
            caption="Просрочено"
            :calculate-cell-value="expiredDisplay"
            :allow-sorting="false"
          />
          <DxColumn
            data-field="fraud_score"
            caption="Fraud score"
            data-type="number"
            :width="110"
            :cell-template="fraudScoreCellTemplate"
          />
          <DxColumn data-field="clicks" caption="Клики" data-type="number" :width="90" />
          <DxColumn
            data-field="click_rate"
            caption="Click rate"
            data-type="number"
            :width="110"
            :customize-text="customizeClickRateText"
          />
          <DxColumn
            data-field="sell_price_per_message"
            caption="Цена продажи"
            :customize-text="customizeUnitPriceText"
          />
          <DxColumn
            data-field="buy_price_per_message"
            caption="Цена закупки"
            :customize-text="customizeUnitPriceText"
          />
          <DxColumn
            data-field="sell_total"
            caption="Продажа"
            :customize-text="customizeMoneyText"
          />
          <DxColumn
            data-field="sell_refund"
            caption="Возмещение продажи"
            :customize-text="customizeMoneyText"
          />
          <DxColumn
            data-field="buy_total"
            caption="Закупка"
            :customize-text="customizeMoneyText"
          />
          <DxColumn
            data-field="buy_refund"
            caption="Возмещение закупки"
            :customize-text="customizeMoneyText"
          />
          <DxColumn
            data-field="spent"
            caption="Потрачено"
            :customize-text="customizeMoneyText"
          />
          <DxColumn
            data-field="profit"
            caption="Прибыль"
            :customize-text="customizeMoneyText"
          />
          <DxSummary :calculate-custom-summary="calculateStatsSummary">
            <DxTotalItem
              name="sum_total"
              summary-type="custom"
              column="total"
              display-format="Итого: {0}"
            />
            <DxTotalItem
              name="sum_in_delivery"
              summary-type="custom"
              show-in-column="col_in_delivery"
              display-format="{0}"
            />
            <DxTotalItem
              name="sum_delivered"
              summary-type="custom"
              show-in-column="col_delivered"
              display-format="{0}"
            />
            <DxTotalItem
              name="sum_undelivered"
              summary-type="custom"
              show-in-column="col_undelivered"
              display-format="{0}"
            />
            <DxTotalItem
              name="sum_expired"
              summary-type="custom"
              show-in-column="col_expired"
              display-format="{0}"
            />
            <DxTotalItem
              name="sum_fraud"
              summary-type="custom"
              column="fraud_score"
              display-format="{0}"
            />
            <DxTotalItem
              name="sum_clicks"
              summary-type="custom"
              column="clicks"
              display-format="{0}"
            />
            <DxTotalItem
              name="sum_click_rate"
              summary-type="custom"
              column="click_rate"
              :customize-text="customizeSummaryClickRate"
            />
            <DxTotalItem
              name="sum_sell_total"
              summary-type="custom"
              column="sell_total"
              :customize-text="customizeSummaryMoney"
            />
            <DxTotalItem
              name="sum_sell_refund"
              summary-type="custom"
              column="sell_refund"
              :customize-text="customizeSummaryMoney"
            />
            <DxTotalItem
              name="sum_buy_total"
              summary-type="custom"
              column="buy_total"
              :customize-text="customizeSummaryMoney"
            />
            <DxTotalItem
              name="sum_buy_refund"
              summary-type="custom"
              column="buy_refund"
              :customize-text="customizeSummaryMoney"
            />
            <DxTotalItem
              name="sum_spent"
              summary-type="custom"
              column="spent"
              :customize-text="customizeSummaryMoney"
            />
            <DxTotalItem
              name="sum_profit"
              summary-type="custom"
              column="profit"
              :customize-text="customizeSummaryMoney"
            />
          </DxSummary>
          <DxPaging :page-size="DEFAULT_GRID_PAGE_SIZE" />
          <DxPager
            :visible="true"
            display-mode="full"
            :show-page-size-selector="true"
            :allowed-page-sizes="[...GRID_PAGE_SIZES]"
          />
        </DxDataGrid>
      </section>

      <section class="stats-section">
        <div class="stats-section__header">
          <h3 class="stats-section__title">Фильтры детализации</h3>
          <DxButton
            text="Применить"
            type="default"
            styling-mode="contained"
            hint="Загрузить детализацию с текущими фильтрами"
            :disabled="!selectedRow"
            @click="applyDetailsFilters"
          />
        </div>
        <DxForm :form-data="filters" :col-count="4" label-location="top" class="stats-filters">
          <DxSimpleItem
            data-field="status"
            editor-type="dxSelectBox"
            :editor-options="{
              items: STATUS_OPTIONS,
              valueExpr: 'value',
              displayExpr: 'text',
              showClearButton: true,
              searchEnabled: true,
              dropDownOptions: { container: 'body' },
            }"
          >
            <DxLabel text="Статус" />
          </DxSimpleItem>
          <DxSimpleItem
            data-field="phone_to"
            editor-type="dxTextBox"
            :editor-options="{ placeholder: 'Номер получателя', showClearButton: true }"
          >
            <DxLabel text="Получатель (phone_to)" />
          </DxSimpleItem>
          <DxSimpleItem
            data-field="phone_from"
            editor-type="dxTextBox"
            :editor-options="{ placeholder: 'Номер / имя отправителя', showClearButton: true }"
          >
            <DxLabel text="Отправитель (phone_from)" />
          </DxSimpleItem>
          <DxSimpleItem
            data-field="user_id"
            editor-type="dxNumberBox"
            :editor-options="{ showClearButton: true, showSpinButtons: true }"
          >
            <DxLabel text="User ID" />
          </DxSimpleItem>
          <DxSimpleItem
            data-field="system_id"
            editor-type="dxTextBox"
            :editor-options="{ showClearButton: true }"
          >
            <DxLabel text="System ID" />
          </DxSimpleItem>
          <DxSimpleItem
            data-field="message_id"
            editor-type="dxTextBox"
            :editor-options="{ showClearButton: true }"
          >
            <DxLabel text="Message ID" />
          </DxSimpleItem>
          <DxSimpleItem
            data-field="correlation_id"
            editor-type="dxTextBox"
            :editor-options="{ showClearButton: true }"
          >
            <DxLabel text="Correlation ID" />
          </DxSimpleItem>
        </DxForm>

        <div class="stats-section__header">
          <h3 class="stats-section__title">{{ detailsCaption }}</h3>
          <DxButton
            text="Экспорт в Excel"
            icon="xlsxfile"
            styling-mode="outlined"
            hint="Экспорт детализации пока недоступен"
            :disabled="true"
          />
        </div>
        <p v-if="unsupportedDetailsGroup.length" class="stats-hint">
          Группировка по {{ unsupportedDetailsGroup.map((f) => GROUP_BY_LABELS[f] ?? f).join(', ') }}
          не сужает детализацию (нет соответствующего фильтра в API).
        </p>
        <p v-else-if="detailTotal > 0" class="stats-hint">
          Разверните строку, чтобы загрузить историю. Нажмите на запись истории, чтобы посмотреть все поля.
        </p>
        <DxDataGrid
          ref="detailsGrid"
          class="stats-details-grid"
          :data-source="detailsStore"
          :remote-operations="{ paging: true, sorting: false, filtering: false }"
          :show-borders="true"
          :row-alternation-enabled="true"
          :column-auto-width="true"
          :allow-column-reordering="true"
          :allow-column-resizing="true"
          :hover-state-enabled="true"
          :paging="paging"
          :pager="pager"
          key-expr="__key"
          no-data-text="Выберите строку статистики, чтобы загрузить детализацию."
        >
          <DxColumnChooser :enabled="true" mode="select" title="Столбцы" />
          <DxStateStoring
            :enabled="true"
            type="localStorage"
            :storage-key="detailsGridStateKey"
          />
          <DxColumn data-field="created_at" caption="Дата" />
          <DxColumn data-field="correlation_id" caption="correlation_id" />
          <DxColumn data-field="status" caption="status" />
          <DxColumn
            data-field="fraud_score"
            caption="Fraud score"
            data-type="number"
            :width="110"
            :cell-template="fraudScoreCellTemplate"
          />
          <DxColumn data-field="phone_from" caption="Отправитель" />
          <DxColumn data-field="phone_to" caption="Получатель" />
          <DxColumn data-field="text" caption="Текст" />
          <DxMasterDetail :enabled="true" template="detail" />
          <template #detail="{ data: row }">
            <MessageDetailHistoryPanel
              :correlation-id="row?.data?.correlation_id ?? row?.correlation_id"
            />
          </template>
        </DxDataGrid>
      </section>
    </div>
  </BottoPagePanel>

  <DxLoadPanel :visible="loading || exporting" :show-indicator="true" :shading="true" />
</template>

<style scoped>
.stats-page {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
}

.stats-filters-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px 12px;
}

.stats-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.stats-field--wide {
  grid-column: 1 / -1;
}

.stats-field > label {
  font-size: 12px;
  font-weight: 500;
  color: var(--botto-color-text-muted, #5a6478);
}

.stats-filters :deep(.dx-form-group-caption) {
  font-size: 13px;
  padding: 4px 0 2px;
}

.stats-filters :deep(.dx-field-item) {
  padding-bottom: 6px;
}

.stats-filters :deep(.dx-form-group-content) {
  padding-top: 4px;
  padding-bottom: 4px;
}

.stats-section {
  margin-top: 16px;
}

.stats-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.stats-section__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--botto-color-text, #15203d);
}

.stats-hint {
  margin: 0 0 10px;
  font-size: 13px;
  color: var(--botto-color-text-muted, #5a6478);
}

.stats-details-grid :deep(.dx-master-detail-row:not(.dx-datagrid-edit-form) .dx-master-detail-cell) {
  background-color: transparent;
}
</style>

<style>
/* Overlay is attached to body — must not be scoped. */
.stats-groupby-overlay.dx-overlay-wrapper {
  z-index: 10000 !important;
}
.stats-groupby-overlay .dx-popup-content {
  padding: 0;
}
</style>
